// Part-2 reviewer-profile ("evaluate") form — July-8 FR-L3: EVERY proposal goes through this
// form, and it opens immediately after Propose/Claim (the proposal stays `incomplete`, hidden
// from AE QC per FR-K15, until this is submitted). July-7 FR-K17: fields auto-populate from what
// we already know and stay fully editable. Angel: expertise field uses "Because of your expertise
// in…" (not "Sub-discipline"); the invite sentence only appears in the justification preview.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deriveJustification, type ProposalView } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { Button, Field, Input, Modal } from "../../components/ui.js";
import { clsx } from "clsx";

export function EvaluateModal({ proposal, onClose }: { proposal: ProposalView; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({
    subdiscipline: proposal.subdiscipline ?? "",
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
    bestMatchUrl: proposal.doiBestMatch ?? "",
    affiliation: proposal.affiliation ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    otherDegree: proposal.otherDegree ?? "",
    title: proposal.title ?? "",
    otherTitles: proposal.otherTitles ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    publicationLink: proposal.linkToPublications ?? proposal.pubMedLink ?? "",
  });
  const save = useMutation({
    mutationFn: () => api.post(`/proposals/${proposal.id}/evaluate`, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const sub = f.subdiscipline.trim();
  const paper = f.bestMatchPaperTitle.trim();
  const justificationReady = !!(sub && paper);
  const justificationPreview = justificationReady
    ? deriveJustification(sub, paper)
    : `Because of your expertise in ${sub || "…"}, and especially your paper "${paper || "…"}", we think you'd be a terrific reviewer for this preprint.`;
  const incomplete =
    !sub ||
    !paper ||
    !f.bestMatchUrl.trim() ||
    !f.secondaryAffiliation.trim() ||
    !f.highestDegree.trim() ||
    !f.title.trim() ||
    !f.linkToProfile.trim() ||
    !f.publicationLink.trim();

  return (
    <Modal open onClose={onClose} title={`Reviewer profile (Part 2) — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Complete the reviewer profile to finish this proposal — it reaches quality check only once this is submitted.
        </p>
        {/* WO-7/21 Track G: the Part-1 identity travels with the form so the profile reads
            complete before submit (name/email are fixed once COI ran). */}
        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
          {proposal.firstName} {proposal.lastName}
          {proposal.reviewerEmail && <span className="ml-2 text-slate-400">{proposal.reviewerEmail}</span>}
        </div>
        <Field label="Affiliation">
          <Input value={f.affiliation} onChange={(e) => setF({ ...f, affiliation: e.target.value })} />
        </Field>
        {/* Angel: label is the invitation lead-in, not "Sub-discipline". */}
        <Field label="Because of your expertise in… (required)">
          <Input
            value={f.subdiscipline}
            onChange={(e) => setF({ ...f, subdiscipline: e.target.value })}
            placeholder="e.g. computational epidemiology"
            required
          />
        </Field>
        <Field label="Best-match paper title (required)">
          <Input
            value={f.bestMatchPaperTitle}
            onChange={(e) => setF({ ...f, bestMatchPaperTitle: e.target.value })}
            required
          />
        </Field>
        <Field label="Best-match paper URL or DOI (required)">
          <Input
            type="url"
            value={f.bestMatchUrl}
            onChange={(e) => setF({ ...f, bestMatchUrl: e.target.value })}
            placeholder="https://doi.org/10.…"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Secondary affiliation (required)">
            <Input
              value={f.secondaryAffiliation}
              onChange={(e) => setF({ ...f, secondaryAffiliation: e.target.value })}
              required
            />
          </Field>
          <Field label="Highest degree (required)">
            <Input value={f.highestDegree} onChange={(e) => setF({ ...f, highestDegree: e.target.value })} required />
          </Field>
        </div>
        <Field label="Other Degrees">
          <Input value={f.otherDegree} onChange={(e) => setF({ ...f, otherDegree: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Title (required)">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          </Field>
          <Field label="Other titles">
            <Input value={f.otherTitles} onChange={(e) => setF({ ...f, otherTitles: e.target.value })} />
          </Field>
        </div>
        <Field label="Institutional profile (link) (required)">
          <Input
            value={f.linkToProfile}
            onChange={(e) => setF({ ...f, linkToProfile: e.target.value })}
            placeholder="https://… faculty or lab page"
            required
          />
        </Field>
        <Field label="Publication / PubMed link (required)">
          <Input
            value={f.publicationLink}
            onChange={(e) => setF({ ...f, publicationLink: e.target.value })}
            placeholder="https://pubmed.ncbi.nlm.nih.gov/…"
            required
          />
        </Field>

        {/* Invite email sentence — live preview only (FR-2.12 / Hildy nudge). */}
        <div className="rounded-md border border-blue-100 bg-blue-50 p-2.5 text-sm text-blue-950">
          <span className="text-xs font-semibold text-blue-900">Justification preview</span>
          <p className="mt-1 text-xs text-blue-800/80">
            This sentence is copied directly into the reviewer invitation email — read it aloud and fix anything that
            sounds awkward before submitting.
          </p>
          <p className={clsx("mt-1.5 italic", justificationReady ? "text-blue-950" : "text-blue-800/60")}>
            {justificationPreview}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Finish later
          </Button>
          <Button onClick={() => save.mutate()} disabled={incomplete || save.isPending}>
            {save.isPending ? "Submitting…" : "Submit reviewer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
